import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";

const links = [
  { id: "projects", label: "Projects", to: "/projects" },
  { id: "about", label: "About Us" },
  { id: "inquiries", label: "Inquiries" },
];

export default function Nav({ variant = "auto" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = location.pathname === "/";
  // variant: 'light' = dark text on light bg; 'dark' = cream text on dark bg
  const tone = variant === "dark" ? "dark" : "light";

  const handleNav = (link) => {
    setOpen(false);
    if (link.to) {
      if (location.pathname === link.to) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(link.to);
      }
      return;
    }
    const id = link.id;
    if (onHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const isDark = tone === "dark";
  const textColor = isDark ? "text-nyg-cream" : "text-black";
  const hoverColor = isDark ? "hover:text-nyg-cream/60" : "hover:text-black/50";
  const bgClass = isDark
    ? scrolled
      ? "bg-nyg-dark/95 backdrop-blur-md border-b border-nyg-cream/10"
      : "bg-transparent"
    : scrolled
      ? "bg-white/90 backdrop-blur-md border-b border-black/5"
      : "bg-transparent";

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgClass}`}
      data-testid="site-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 h-20 md:h-24 flex items-center justify-between">
        <button onClick={goHome} data-testid="nav-logo" className="block">
          <Logo size={44} tone={isDark ? "cream" : "dark"} />
        </button>

        <nav className="hidden md:flex items-center gap-12">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleNav(l)}
              data-testid={`nav-link-${l.id}`}
              className={`label ${textColor} ${hoverColor} transition-colors duration-300`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          onClick={() => setOpen((o) => !o)}
          className={`md:hidden ${textColor}`}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X strokeWidth={1} size={26} /> : <Menu strokeWidth={1} size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className={`md:hidden overflow-hidden ${
              isDark
                ? "bg-nyg-dark border-t border-nyg-cream/10"
                : "bg-white border-t border-black/5"
            }`}
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleNav(l)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={`label text-left ${textColor}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
